/**
 * Secure error handling - prevents exposure of sensitive information
 * Following OWASP guidelines for error handling
 */

const isProduction = import.meta.env.PROD;

/**
 * Sanitize error messages for client-side display
 * Never exposes stack traces, file paths, or sensitive information
 * Extracts user-friendly messages from Zod validation errors
 */
export function sanitizeError(error: unknown): string {
  // Handle Zod validation errors (ZodError)
  if (error && typeof error === "object" && "errors" in error) {
    const zodError = error as { errors: Array<{ message: string; path?: (string | number)[] }> };
    if (Array.isArray(zodError.errors) && zodError.errors.length > 0) {
      // Return the first error message (most relevant)
      return zodError.errors[0].message;
    }
  }
  
  // Handle Error objects
  if (error instanceof Error) {
    // In production, return generic messages
    if (isProduction) {
      // Map common error types to user-friendly messages
      if (error.message.includes("network") || error.message.includes("fetch")) {
        return "Network error. Please check your connection and try again.";
      }
      if (error.message.includes("auth") || error.message.includes("unauthorized")) {
        return "Authentication failed. Please try again.";
      }
      if (error.message.includes("validation") || error.message.includes("invalid")) {
        return "Invalid input. Please check your data and try again.";
      }
      if (error.message.includes("not found")) {
        return "Resource not found.";
      }
      if (error.message.includes("permission") || error.message.includes("forbidden")) {
        return "You don't have permission to perform this action.";
      }
      
      // Generic fallback for production
      return "An error occurred. Please try again later.";
    }
    
    // In development, return the actual error message (but still no stack trace)
    return error.message;
  }
  
  // Handle string errors
  if (typeof error === "string") {
    return isProduction ? "An error occurred. Please try again later." : error;
  }
  
  // Handle unknown error types
  return "An unexpected error occurred. Please try again.";
}

/**
 * Log error securely (server-side only)
 * This should be called from edge functions or server-side code
 */
export function logError(error: unknown, context?: Record<string, any>): void {
  // Only log in development or if explicitly enabled
  if (!isProduction || import.meta.env.VITE_ENABLE_ERROR_LOGGING === "true") {
    const errorInfo: any = {
      timestamp: new Date().toISOString(),
      context: context || {},
    };
    
    if (error instanceof Error) {
      errorInfo.message = error.message;
      errorInfo.name = error.name;
      // Only include stack trace in development
      if (!isProduction) {
        errorInfo.stack = error.stack;
      }
    } else {
      errorInfo.error = String(error);
    }
    
    console.error("Error logged:", errorInfo);
  }
}

/**
 * Create a safe error response for API calls
 */
export function createErrorResponse(
  error: unknown,
  statusCode: number = 500,
  context?: Record<string, any>
): Response {
  const message = sanitizeError(error);
  
  // Log the full error server-side (this would be in edge functions)
  if (context) {
    logError(error, context);
  }
  
  return new Response(
    JSON.stringify({
      success: false,
      error: message,
    }),
    {
      status: statusCode,
      headers: {
        "Content-Type": "application/json",
      },
    }
  );
}

/**
 * Validate that error doesn't contain sensitive information
 */
export function validateErrorSafety(errorMessage: string): boolean {
  const sensitivePatterns = [
    /password/i,
    /secret/i,
    /token/i,
    /key/i,
    /api[_-]?key/i,
    /private/i,
    /\.env/i,
    /\/etc\//i,
    /\/var\//i,
    /\/home\//i,
    /C:\\/i,
    /D:\\/i,
    /stack\s+trace/i,
    /at\s+\w+\.\w+/i, // Stack trace patterns
  ];
  
  return !sensitivePatterns.some((pattern) => pattern.test(errorMessage));
}

